import { KNOWN_LESSON_INSTRUCTORS } from "@/lib/config/lessonInstructors";
import { YANG_TAEWOOK_LESSON_TYPES } from "@/lib/lesson/lessonPricing";

export interface LessonInstructorProfileSeed {
  uid: string;
  name: string;
  nickname?: string;
  photoUrl: string;
  tagline: string;
  bio: string;
  specialty: string[];
  tags: string[];
  targetAudience: string[];
  differentiator: string;
  experience: string;
  location: string;
  services: string[];
  lessonTypes?: typeof YANG_TAEWOOK_LESSON_TYPES;
}

export const LESSON_INSTRUCTOR_PROFILES: Record<
  string,
  LessonInstructorProfileSeed
> = {
  양태욱: {
    uid: KNOWN_LESSON_INSTRUCTORS.양태욱.uid,
    name: "양태욱",
    nickname: KNOWN_LESSON_INSTRUCTORS.양태욱.nickname,
    photoUrl: "/instructors/yang-taewook-placeholder.svg",
    tagline: "K-Pop · 오디션 보컬 전문 | 디렉터 시선의 실전 코칭",
    bio: "입시·오디션·데뷔를 준비하는 보컬에게, K-Pop과 오디션 현장 기준으로 코칭합니다. 단순 발성 연습을 넘어, 곡 해석부터 무대 퍼포먼스까지 디렉터 관점에서 함께 봅니다.",
    specialty: ["보컬", "K-Pop", "오디션 보컬"],
    tags: ["K-Pop", "오디션", "입시", "데뷔 준비", "곡 해석", "무대"],
    targetAudience: ["입시", "오디션", "데뷔 준비생"],
    differentiator: "디렉터 시선으로 곡 해석·무대 퍼포먼스까지",
    experience: [
      "SM아카데미 보컬 트레이너 (2008–2013)",
      "블루밍 사운드 보컬 디렉터",
      "파워보컬 실용음악학원 · 베스트보컬 보컬 코치",
      "현) 엔터테인먼트 보컬 디렉터 & 보컬 코치",
      "동국대학교 문화예술대학원 실용음악학과 보컬 전공",
    ].join("\n"),
    location: "서울",
    services: ["레슨", "피드백"],
    lessonTypes: YANG_TAEWOOK_LESSON_TYPES,
  },
  김한나: {
    uid: KNOWN_LESSON_INSTRUCTORS.김한나.uid,
    name: "김한나",
    nickname: KNOWN_LESSON_INSTRUCTORS.김한나.nickname,
    photoUrl: "/instructors/kim-hanna.png",
    tagline: "보컬 방향성 · 강점 발견 · 입시 실전 전문 코칭",
    bio: "수강생의 음악적 방향성과 고유한 강점을 함께 찾고, 딕션·발성·자세 등 보컬의 디테일을 세심하게 보완합니다. 다양한 장르의 이해와 심화 학습, 입시 면접 준비까지 개인의 목표에 맞춰 단계별로 지도합니다.",
    specialty: ["보컬", "입시 보컬", "K-Pop"],
    tags: ["발성", "딕션", "장르 해석", "입시", "면접", "강점 발견"],
    targetAudience: ["보컬 입시생", "오디션 준비생", "자신만의 보컬 색깔을 찾는 분"],
    differentiator: "음악적 방향성과 강점을 먼저 찾고 디테일까지 완성하는 맞춤 코칭",
    experience: [
      "백제예술대학교 실용음악과 보컬 전공",
      "동아방송예술대학교 실용음악계열 성악 전공",
      "전) 밸런스뮤직 출강",
      "현) 에토즈 실용음악학원 · 레인 실용음악학원 출강",
      "SBS 판타스틱 듀오 시즌 1 이현우 편 최종 1인",
      "SBS K팝스타 시즌 3 본선 진출",
      "KBS 더 딴따라 본선 진출",
      "록킹돌 Ahri(아리) 'Eyes On Me' 가이드 보컬",
      "Jin '이렇게 아직 널 잊지 못해서 그래' 피처링",
      "RBW 창의인재동반사업 '사계절' 메인 보컬",
      "드라마 '사장돌 마트' OST 가이드 보컬",
      "슈에 '있잖아, 나' 보컬 참여",
      "동아방송예술대학교 '활주로' · '비상' 메인 무대",
      "그 외 공연·녹음 다수",
    ].join("\n"),
    location: "서울",
    services: ["레슨", "피드백"],
  },
};

export function getLessonInstructorProfileSeed(
  name: string
): LessonInstructorProfileSeed | null {
  return LESSON_INSTRUCTOR_PROFILES[name] ?? null;
}

export function mapSeedToLessonInstructor(
  seed: LessonInstructorProfileSeed
): {
  id: string;
  name: string;
  photo: string;
  specialty: string;
  instruments: string[];
  pricePerHour: number;
  rating: number;
  reviewCount: number;
  location: string;
  experience: string;
  bio: string;
  certifications: string[];
  tags: string[];
  availability: string[];
  lessonTypes: NonNullable<LessonInstructorProfileSeed["lessonTypes"]>;
  userId: string;
  tagline?: string;
} {
  return {
    id: seed.uid,
    name: seed.name,
    photo: seed.photoUrl,
    specialty: seed.specialty.join(", "),
    instruments: seed.specialty,
    pricePerHour: seed.lessonTypes?.[0]?.price ?? 0,
    rating: 0,
    reviewCount: 0,
    location: seed.location,
    experience: seed.experience,
    bio: seed.bio,
    certifications: [],
    tags: seed.tags,
    availability: [],
    lessonTypes: seed.lessonTypes ?? [],
    userId: seed.uid,
    tagline: seed.tagline,
  };
}

/** Firestore 문서에 비어 있는 필드만 시드 데이터로 채웁니다. */
export function mergeLessonInstructorProfile<T extends Record<string, unknown>>(
  name: string,
  data: T
): T & Partial<LessonInstructorProfileSeed> {
  const seed = getLessonInstructorProfileSeed(name);
  if (!seed) return data;

  return {
    ...data,
    photoUrl: (data.photoUrl as string) || seed.photoUrl,
    bio: (data.bio as string) || seed.bio,
    experience: (data.experience as string) || seed.experience,
    location: (data.location as string) || seed.location,
    specialty:
      Array.isArray(data.specialty) && (data.specialty as string[]).length > 0
        ? (data.specialty as string[])
        : seed.specialty,
    tags:
      Array.isArray(data.tags) && (data.tags as string[]).length > 0
        ? (data.tags as string[])
        : seed.tags,
    tagline: (data.tagline as string) || seed.tagline,
    targetAudience:
      Array.isArray(data.targetAudience) &&
      (data.targetAudience as string[]).length > 0
        ? (data.targetAudience as string[])
        : seed.targetAudience,
    differentiator:
      (data.differentiator as string) || seed.differentiator,
    services:
      Array.isArray(data.services) && (data.services as string[]).length > 0
        ? (data.services as string[])
        : seed.services,
    lessonTypes:
      Array.isArray(data.lessonTypes) && (data.lessonTypes as unknown[]).length > 0
        ? (data.lessonTypes as LessonInstructorProfileSeed["lessonTypes"])
        : seed.lessonTypes,
  };
}
